import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass } from "@phosphor-icons/react";
import api from "@/lib/apiClient";
import { TierBadge, timeAgo, STATUS_STYLES } from "./helpers";

const TIER_FILTERS = ["all", "hot", "warm", "cold"];
const TYPE_FILTERS = ["all", "buyer", "seller"];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [tier, setTier] = useState("all");
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (tier !== "all") params.tier = tier;
    if (type !== "all") params.lead_type = type;
    try {
      setError(false);
      const { data } = await api.get("/leads", { params });
      setLeads(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [tier, type]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = leads.filter(
    (l) =>
      !q ||
      l.name?.toLowerCase().includes(q.toLowerCase()) ||
      l.email?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div>
        <h1 className="text-3xl text-white">Leads</h1>
        <p className="text-white/50 text-sm mt-1">{filtered.length} leads in view</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {TIER_FILTERS.map((t) => (
            <button
              key={t}
              data-testid={`filter-tier-${t}`}
              onClick={() => setTier(t)}
              className={`px-4 py-2 rounded-sm text-xs uppercase tracking-wider border transition-colors ${
                tier === t ? "border-brand text-brand bg-brand/10" : "border-white/10 text-white/50 hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
          <span className="w-px bg-white/10 mx-1" />
          {TYPE_FILTERS.map((t) => (
            <button
              key={t}
              data-testid={`filter-type-${t}`}
              onClick={() => setType(t)}
              className={`px-4 py-2 rounded-sm text-xs uppercase tracking-wider border transition-colors ${
                type === t ? "border-brand text-brand bg-brand/10" : "border-white/10 text-white/50 hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-3 text-white/40" />
          <input
            data-testid="leads-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or email"
            className="bg-[#101116] border border-white/10 rounded-sm pl-9 pr-4 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-brand/50 w-full sm:w-64"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#101116] border border-white/5 rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-white/5 text-[11px] uppercase tracking-[0.15em] text-white/40 sticky top-0 bg-[#101116]">
          <span className="col-span-4">Lead</span>
          <span className="col-span-2 hidden sm:block">Type</span>
          <span className="col-span-2 hidden md:block">Timeline</span>
          <span className="col-span-2">Status</span>
          <span className="col-span-2 text-right">Score</span>
        </div>
        {loading ? (
          <p className="px-6 py-12 text-center text-white/40 text-sm">Loading…</p>
        ) : error ? (
          <div className="px-6 py-12 text-center" data-testid="leads-error">
            <p className="text-white/50 text-sm mb-3">Couldn't load leads right now.</p>
            <button onClick={load} className="text-brand text-sm hover:text-brand-hover">Try again</button>
          </div>
        ) : filtered.length === 0 ? (
          <p className="px-6 py-12 text-center text-white/40 text-sm">No leads match your filters.</p>
        ) : (
          <div className="divide-y divide-white/5">
            {filtered.map((l) => (
              <Link
                key={l.id}
                to={`/dashboard/leads/${l.id}`}
                data-testid={`lead-row-${l.id}`}
                className="grid grid-cols-12 items-center px-6 py-4 hover:bg-white/5 transition-colors"
              >
                <div className="col-span-4 min-w-0">
                  <p className="text-white truncate">{l.name}</p>
                  <p className="text-xs text-white/40 truncate">{l.email}</p>
                </div>
                <span className="col-span-2 hidden sm:block text-sm text-white/60 capitalize">{l.lead_type}</span>
                <span className="col-span-2 hidden md:block text-sm text-white/60">{l.timeline || "—"}</span>
                <span className={`col-span-2 text-xs uppercase tracking-wider capitalize ${STATUS_STYLES[l.status] || "text-white/50"}`}>
                  {l.status}
                </span>
                <div className="col-span-2 flex items-center justify-end gap-3">
                  {l.score_numeric != null && (
                    <span className="text-sm text-white/50 tabular-nums hidden sm:inline">{l.score_numeric}</span>
                  )}
                  <TierBadge tier={l.score_tier} pending={l.ai_status === "pending"} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
